import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    ReportTemplateTypeFieldsFragmentDoc,
    ReportTemplatesDocument,
    type ReportTemplatesQuery,
    type ReportTemplatesQueryVariables,
} from '@/graphql/types';
import { useFragment as readFragmentData } from '../../../../../../gen/fragment-masking';
import type { ElementDefinition } from '../../../elements/types';
import { parseDefinitions } from '../../definitionParsing';
import type { RuntimeDataServiceInput } from '../contracts';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularColumnDescriptor,
} from '../types';
import { isRecord, normalizeRequiredId } from '../validationPrimitives';

function validateTemplateResolveInput(input: unknown): RuntimeDataServiceInput<'template.resolve'> {
    if (!isRecord(input)) {
        throw new Error('Expected "template.resolve" input to be an object.');
    }

    return {
        templateId: normalizeRequiredId(input.templateId, 'template.resolve.templateId'),
    };
}

function validateTemplateResolveResult(result: unknown): ElementDefinition[] {
    return parseDefinitions(result, ['runtime_data_service(template.resolve)']);
}

function stringifyTemplateStructureSettings(value: unknown): string {
    if (value === null || typeof value === 'undefined') {
        return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    return JSON.stringify(value);
}

function createTemplateStructureColumns() {
    return [
        {
            kind: 'text',
            key: 'type',
            label: gettext('ELEMENT_TYPE'),
            defaultAlignment: 'left',
            resolveCell: (entry: ElementDefinition) => entry.type,
        },
        {
            kind: 'text',
            key: 'settings',
            label: gettext('SETTINGS'),
            defaultAlignment: 'left',
            resolveCell: (entry: ElementDefinition) =>
                stringifyTemplateStructureSettings(entry.settings),
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<ElementDefinition>[];
}

export class TemplateResolveRuntimeDataService extends RuntimeDataService<'template.resolve'> {
    static readonly serviceId = 'template.resolve' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_TEMPLATE_RESOLVE_LABEL'),
            description: gettext('DATA_SERVICE_TEMPLATE_RESOLVE_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'structure',
                label: gettext('DATA_SERVICE_TEMPLATE_RESOLVE_TEMPLATE_STRUCTURE_LABEL'),
                description: gettext(
                    'DATA_SERVICE_TEMPLATE_RESOLVE_TEMPLATE_STRUCTURE_DESCRIPTION'
                ),
                supportedContextKinds: ['project', 'measuring-point'],
                supportedSwarmTypes: null,
                path: '',
                valueKind: 'tabular',
                argPolicy: 'required',
                exposeInInsertionPresets: false,
                columns: createTemplateStructureColumns(),
                parseTableRows: validateTemplateResolveResult,
                buildInput: ({ arg, templateId }) => {
                    if (arg !== null) {
                        return { templateId: arg };
                    }

                    if (templateId) {
                        return { templateId };
                    }

                    throw new Error(
                        'Template resolve binding requires "arg" or a runtime template id.'
                    );
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'template.resolve',
            ElementDefinition
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateTemplateResolveInput;

    validateResult = validateTemplateResolveResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'template.resolve'>) => {
        const templateIdAsString = String(input.templateId);
        const variables: ReportTemplatesQueryVariables = {
            ids: [templateIdAsString],
            first: 1,
        };

        return from(
            this.client.query<ReportTemplatesQuery, ReportTemplatesQueryVariables>({
                query: ReportTemplatesDocument,
                variables,
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data?.reportTemplates) {
                    throw new Error(
                        'Missing reportTemplates payload while resolving template include.'
                    );
                }

                const matchingNodes = data.reportTemplates.edges
                    .filter((edge): edge is NonNullable<typeof edge> => edge !== null)
                    .map((edge) => edge.node)
                    .filter((node): node is NonNullable<typeof node> => node !== null);

                if (matchingNodes.length !== 1) {
                    throw new Error(
                        `Expected exactly one template for include id "${templateIdAsString}", received ${matchingNodes.length}.`
                    );
                }

                const template = readFragmentData(
                    ReportTemplateTypeFieldsFragmentDoc,
                    matchingNodes[0]
                );

                let parsedStructure: unknown;
                try {
                    parsedStructure = JSON.parse(template.structure);
                } catch (error) {
                    const detail = error instanceof Error ? error.message : String(error);
                    throw new Error(
                        `Invalid template structure JSON for include id "${templateIdAsString}": ${detail}`
                    );
                }

                return parseDefinitions(parsedStructure, [
                    `template_include(${templateIdAsString})`,
                ]);
            })
        );
    };
}

export function createTemplateResolveService(
    client: ApolloClient<object>
): TemplateResolveRuntimeDataService {
    return new TemplateResolveRuntimeDataService(client);
}
